/* eslint-disable no-continue */
import { v4 as uuidv4 } from 'uuid';
import { ParsedSifData, SifItem, SIFObject, SifOption } from './types';
import { IAditionalFields, IRecordField, IRecordFieldsList } from '../types';
import { format } from 'date-fns';
import { ListValue } from 'views/backoffice/CustomLists/types';
import axios from 'axios';
import { CreateDynamicObjectPayload, IObjectRaw } from 'views/backoffice/CustomObjects/types';
import { ILineItemHeader, LineItemRowItemV2Input, LineItemRowV2Input, ILineItemsFields } from '../LineItems/types';
import { client } from 'graphql/config';
import { QUERY_ME } from 'graphql/mutations/auth';
import { IUserDataToken } from 'utils/types';

export const EXCLUDED_COLS = ['__check__', '__detail_panel_toggle__', 'recordViewerUrl', 'dataFocusViewUrl', 'actions', 'updatedAt'];

export const SIF_IDENTIFIERS: SIFObject = {
    SF: 'SIF File Name',
    VR: 'Version',
    DT: 'Date',
    TM: 'Time',
    PT: 'Total Product',
    TL: 'Total List',
    TS: 'Total Sell',
    TP: 'Total Purchase',
    PN: 'Product Number',
    PD: 'Product Description',
    MC: 'Catalog Code',
    EC: 'Manufacturer Code',
    TG: 'Tag 1',
    GC: 'Tag 2',
    T3: 'Tag 3',
    T4: 'Tag 4',
    T5: 'Tag 5',
    QT: 'Line Item Quantity',
    PL: 'Product List',
    SP: 'Product Sell',
    BP: 'Product Cost',
    'S-': 'Sell Discount',
    'P%': 'Purchase Discount',
    ON: 'Option Number',
    OD: 'Option Description',
    OL: 'Option List Up charge',
    OG: 'Option Group',
    SL: 'End of SIF',
    FPPRJ: 'SIF File Path',
    L1: 'Alias One Tag',
    L2: 'Alias Two Tag',
    L3: 'Alias Three Tag',
    PS: 'Product Sell',
    PB: 'Product Cost'
};

const getReadableKey = (key: string) => SIF_IDENTIFIERS[key] || key;

/**
 * Parses the provided SIF file content into a structured JavaScript object.
 *
 * @param {string} sifContent The raw text content of the SIF file.
 * @returns {Object} An array of line items of item SifItem.
 */

const parseSifFile = (sifContent: string): SifItem[] => {
    const lines = sifContent
        .split('\n') // Split by new line
        .map((line) => line.trim()) // Trim whitespace from each line
        .filter((line) => line.length > 0 && !line.startsWith('#')); // Remove empty lines and comments

    const parsedData: ParsedSifData = {
        header: {},
        items: []
    };

    let currentItem: SifItem | null = null;
    let isHeaderSection = true; // Flag to identify the header section
    let optionGroupCounter = 0; // To group ON, OD, OG, OL lines
    const usedItemIds = new Set<number>(); // Track used item IDs to ensure uniqueness

    // Helper function to generate unique item ID
    const generateUniqueItemId = (): number => {
        let itemId: number;
        do {
            itemId = Math.floor(Math.random() * 1000000) + 1; // Range: 1 to 1,000,000
        } while (usedItemIds.has(itemId));
        usedItemIds.add(itemId);
        return itemId;
    };

    for (let i = 0; i < lines.length; i += 1) {
        const line = lines[i];
        const parts = line.split('=');

        // Skip lines that don't have a key=value format (e.g., summary line, empty lines after filter)
        if (parts.length < 2) continue;
        if (line.startsWith('SL=')) break;

        const originalKey = parts[0].trim();
        const readableKey = getReadableKey(originalKey); // Get the readable key
        const value = parts.slice(1).join('=').trim(); // Handle values that might contain '='

        // --- Header Section Parsing ---
        if (isHeaderSection) {
            // The first 'PN=' indicates the end of the header and start of items
            if (originalKey === 'PN') {
                isHeaderSection = false;
                currentItem = { options: [] }; // Initialize first item
                currentItem[readableKey] = value; // Add PN to current item using readable key
            } else parsedData.header[readableKey] = value; // Use readable key for header
            continue; // Move to the next line
        }

        // --- Item and Options Section Parsing ---
        if (!isHeaderSection) {
            // If we encounter a new 'PN', it means the previous item has ended
            if (originalKey === 'PN') {
                if (currentItem) {
                    parsedData.items.push({ ...currentItem, id: generateUniqueItemId() }); // Store the completed item with unique ID
                }
                currentItem = { options: [] }; // Start a new item
                optionGroupCounter = 0; // Reset option counter for new item
            }

            if (currentItem) {
                if (originalKey === 'ON' || originalKey === 'OD' || originalKey === 'OG' || originalKey === 'OL') {
                    // Process options (ON, OD, OG, OL always come in a block of 4 for one option)
                    if (optionGroupCounter % 4 === 0) {
                        currentItem.options.push({}); // Add a new option object
                    }
                    const currentOption = currentItem.options[currentItem.options.length - 1];
                    currentOption[readableKey] = value; // Use readable key for options
                    optionGroupCounter += 1;
                } else {
                    // Standard item attributes
                    currentItem[readableKey] = value; // Use readable key for item attributes
                    // Apply type conversion for known numeric fields using original keys for check
                    if (['QT', 'PL', 'SP', 'BP', 'S-', 'P%'].includes(originalKey)) {
                        currentItem[readableKey] = parseFloat(value);
                    }
                }
            }
        }
    }

    // After loop, push the last currentItem if it exists
    if (currentItem) {
        parsedData.items.push({ ...currentItem, id: generateUniqueItemId() });
    }

    // Convert 'NR' in header to a number if it exists and use its readable key
    if (parsedData.header[getReadableKey('NR')]) {
        parsedData.header[getReadableKey('NR')] = parseInt(parsedData.header[getReadableKey('NR')] as string, 10);
    }

    return parsedData.items.map((item) => {
        let combinedOptionNumber = '';
        let combinedOptionDescription = '';
        let combinedOptionGroup = '';
        if (item.options.length > 0) {
            combinedOptionNumber = item.options
                .filter((option) => option['Option Number'])
                .map((option) => option['Option Number'])
                .join(',');
            combinedOptionDescription = item.options
                .filter((option) => option['Option Description'])
                .map((option) => option['Option Description'])
                .join(',');
            combinedOptionGroup = item.options
                .filter((option) => option['Option Group'])
                .map((option) => option['Option Group'])
                .join(',');
        }
        return {
            ...item,
            options: item.options?.map((option, idx, arr) => {
                let tempId: number;
                const usedIds = new Set(arr.slice(0, idx).map((opt) => String(opt.tempId)));
                do {
                    tempId = Math.floor(Math.random() * 10000) + 1;
                } while (usedIds.has(String(tempId)));

                return {
                    ...option,
                    tempId
                };
            }),
            'Option Number': combinedOptionNumber,
            'Option Description': combinedOptionDescription,
            'Option Group': combinedOptionGroup
        };
    });
};

export const getRowsFromSif = (sifFile: File, setter?: (data: any[]) => void) => {
    const reader = new FileReader();
    reader.onload = (e) => {
        const content = e.target?.result;
        if (typeof content === 'string') {
            const parsedRows = parseSifFile(content);
            setter?.(parsedRows);
        }
    };
    reader.readAsText(sifFile);
};

export const getRowsFromSifLambda = (sifFile: File, setter?: (data: any) => void) => {
    const reader = new FileReader();
    reader.onload = (e) => {
        const content = e.target?.result;
        if (typeof content === 'string') {
            const parsedRows = generateLambdaSifRows(content);
            setter?.(parsedRows);
        }
    };
    reader.readAsText(sifFile);
};

export const generateLambdaSifRows = async (content: string, additionalFieldDefinitions?: IAditionalFields) => {
    try {
        console.log('content', { content });
        const lambdaParserUrl = process.env.REACT_APP_SIF_LAMBDA_URL || 'https://008l1vv8k1.execute-api.us-east-1.amazonaws.com/sif';
        const res = await axios({
            method: 'get',
            url: lambdaParserUrl,
            data: {
                sifFileContent: content,
                additionalFieldDefinitions: additionalFieldDefinitions || [
                    { id: 857, name: 'Option Group', dataType: 'string', sifCodes: ['OG'] },
                    { id: 856, name: 'Option Description', dataType: 'string', sifCodes: ['OD'] },
                    { id: 855, name: 'Option Number', dataType: 'string', sifCodes: ['ON'] },
                    { id: 854, name: 'Tag 5', dataType: 'string', sifCodes: ['T5', 'L3'] },
                    { id: 853, name: 'Tag 4', dataType: 'string', sifCodes: ['T4', 'L2'] },
                    { id: 852, name: 'Tag 3', dataType: 'string', sifCodes: ['T3', 'L1'] },
                    { id: 851, name: 'Tag 2', dataType: 'string', sifCodes: ['GC'] },
                    { id: 850, name: 'Tag 1', dataType: 'string', sifCodes: ['TG'] },
                    { id: 849, name: 'Purchase Discount', dataType: 'number', sifCodes: ['P%'] },
                    { id: 848, name: 'Sale Discount', dataType: 'number', sifCodes: ['S-'] },
                    { id: 847, name: 'Product Cost', dataType: 'number', sifCodes: ['BP', 'PB'] },
                    { id: 846, name: 'Product Sell', dataType: 'number', sifCodes: ['SP', 'PS'] },
                    { id: 845, name: 'Product List', dataType: 'number', sifCodes: ['PL'] },
                    { id: 844, name: 'Quantity', dataType: 'number', sifCodes: ['QT'] },
                    { id: 843, name: 'Manufacturer Code (Spec/Configura)', dataType: 'string', sifCodes: ['EC'] },
                    { id: 842, name: 'Catalog Code', dataType: 'string', sifCodes: ['MC'] },
                    { id: 841, name: 'Product Description', dataType: 'string', sifCodes: ['PD'] },
                    { id: 840, name: 'Product Number', dataType: 'string', sifCodes: ['PN'] },
                    { id: 839, name: 'Total Product (Spec/Configura)', dataType: 'string', sifCodes: ['PT'] },
                    { id: 838, name: 'Total Purchase (Spec/Configura)', dataType: 'string', sifCodes: ['TP'] },
                    { id: 837, name: 'Total Sell (Spec/Configura)', dataType: 'string', sifCodes: ['TS'] },
                    { id: 836, name: 'Total List (Spec/Configura)', dataType: 'string', sifCodes: ['TL'] },
                    { id: 835, name: 'SIF File Path (Cyncly)', dataType: 'string', sifCodes: ['FPPRJ'] },
                    { id: 834, name: 'Creation Time (Spec/Configura)', dataType: 'string', sifCodes: ['TM'] },
                    { id: 833, name: 'Creation Date (Spec/Configura)', dataType: 'string', sifCodes: ['DT'] },
                    { id: 832, name: 'Version (Spec/Configura)', dataType: 'string', sifCodes: ['VR'] },
                    { id: 831, name: 'SIF File Name', dataType: 'string', sifCodes: ['SF'] }
                ]
            }
        });
        console.log('res.data', res.data);
    } catch (error) {
        console.log('Error calling SIF Lambda parser:', error);
    }
};

/* deprecated - use new parser 
const parseSifData = (sifData: string) => {
    const lines = sifData.split('\n');
    const objects: any[] = [];
    let currentObject: any = null;

    lines.forEach((line) => {
        const [key, value] = line.split('=');

        if (key === 'PN') {
            if (currentObject) objects.push(currentObject);

            currentObject = { id: uuidv4(), [SIF_IDENTIFIERS.PN]: value?.replace(/\r/g, '').trim() };
        } else if (key === 'SL') {
            if (currentObject) {
                objects.push(currentObject);
                currentObject = null;
            }
        } else if (currentObject && key) {
            currentObject[SIF_IDENTIFIERS[key] || key] = value?.replace(/\r/g, '').trim() || '';
        }
    });

    if (currentObject) {
        objects.push(currentObject);
    }

    return objects;
};
*/

export const generateDefaultRecord = async (
    recordTypeId: number,
    headers: IRecordField[],
    listValues: Record<number, ListValue[]>,
    userId: number
) => {
    let userData: IUserDataToken | null = null;
    try {
        const { data } = await client.query({
            query: QUERY_ME,
            fetchPolicy: 'network-only',
            errorPolicy: 'all'
        });
        userData = data?.me ? (JSON.parse(data.me) as IUserDataToken) : null;
    } catch {
        userData = null;
    }
    if (!userData) return undefined;

    const recordData = {
        id: uuidv4(),
        recordType: recordTypeId,
        createdBy: userId,
        enabled: true
    } as Record<string, any>;
    for (const header of headers) {
        if (header.required && header.field) {
            if (header.dataType === 'number') recordData[header.field] = 0;
            if (header.dataType === 'string' || header.dataType === 'textarea') recordData[header.field] = 'N/A';
            if (header.dataType === 'datetime') recordData[header.field] = new Date().toISOString();
            if (header.dataType === 'date') recordData[header.field] = format(new Date(), 'yyyy-MM-dd');
            if (header.dataType === 'currency') recordData[header.field] = 0.0;
            if ((header.dataType === 'dropdown' || header.dataType === 'single-select') && header.listType?.id)
                recordData[header.field] = listValues[+header.listType?.id]?.find((val) => val.enabled)?.id || '';
            if (header.dataType === 'checkbox') recordData[header.field] = 'false';
            if ((header.dataType === 'multiselect' || header.dataType === 'multiselect-checkbox') && header.listType?.id)
                recordData[header.field] = listValues[+header.listType?.id]?.find((val) => val.enabled)?.id
                    ? `[${Number(listValues[+header.listType?.id]?.find((val) => val.enabled)?.id)}]`
                    : '';
            if (header.dataType === 'url') recordData[header.field] = 'N/A';
            if (header.dataType === 'time') recordData[header.field] = new Date();
            if (header.dataType === 'object') recordData[header.field] = '';
            if (header.dataType === 'attachment') recordData[header.field] = '[]';
            if (header.dataType === 'phone') recordData[header.field] = '[1,111111111]';
            if (header.dataType === 'email') recordData[header.field] = 'placeholder@yourdomain.com';
        }
    }
    return recordData;
};

export const mapLineItemsDataToMutationJSON = (items: Record<string, any>[], recordId: number) =>
    items.map((row) => ({
        index: row?.id || 1,
        recordHeaderId: Number(recordId),
        lineItemsFields: Object.keys(row)
            .filter((key) => !['options', 'id', 'order'].includes(key) && key.includes('-') && row[key])
            .map((key) => ({
                lineItemsByTypeFiledsId: Number(key.split('-')?.at(-1)),
                value: row[key]?.toString()
            }))
    }));

export const generateDataGridObjectUpdates = (objectsToCreate: Record<string, any[]>, headers: IRecordFieldsList | undefined) => {
    const newRowHeaders: Record<string, string> = {};
    const newAdditionalFields: IAditionalFields[] = [];
    if (!Object.keys(objectsToCreate).length) return { newRowHeaders, newAdditionalFields };

    for (const objectKey of Object.keys(objectsToCreate)) {
        const object = objectsToCreate[objectKey];
        if (object && object.length) {
            const objectDummyId = uuidv4();
            newRowHeaders[objectKey] = objectDummyId;
            newAdditionalFields.push({
                id: uuidv4(),
                objectValue: {
                    id: objectDummyId,
                    objectValues: object.map((obj) => ({
                        tag: `${obj.order};${obj.id};${objectDummyId}`,
                        value: obj.value,
                        objectProperty: { ...obj }
                    }))
                },
                tag: `;;${headers?.[String(object)]?.id || ''}`,
                value: objectDummyId
            });
        }
    }
    return { newRowHeaders, newAdditionalFields };
};

export const getFormattedOptionsValue = (options: SifOption[] | null) => {
    if (!options) return '';

    return options
        .map((option) => Object.fromEntries(Object.entries(option)?.filter(([key, val]) => !!val && !['id'].includes(key))))
        .reduce((acc, cur) => `${acc.length ? `${acc}, ` : ''}${Object.values(cur).join(',')}`, '');
};

export const getRequiredLineItemFieldNames = (fields: ILineItemsFields[]) =>
    fields.filter((item) => item?.recordAdditionalFields?.required)?.map((item) => item.recordAdditionalFields.name);

export const getLineItemFieldsByName = (fields: ILineItemsFields[]) =>
    fields.reduce<Record<string, ILineItemsFields>>((acc, field) => {
        acc[field.recordAdditionalFields.name] = field;
        return acc;
    }, {});

/**
 * Generates a payload array for bulk creation of dynamic objects from a list of SifOption.
 * Maps SifOption attributes to the object properties defined in the given objectDefinition.
 * Skips options that have no valid attributes.
 * @param {SifOption[]} options - Array of option objects to generate payloads for.
 * @param {IObjectRaw} objectDefinition - The object definition with property metadata.
 * @returns {CreateDynamicObjectPayload[]} Array of payload objects for bulk creation.
 */
export const generateBulkDynamicObjectPayload = (options: SifOption[], objectDefinition: IObjectRaw): CreateDynamicObjectPayload[] => {
    // TODO: add correct payload type when it's ready
    const payload: CreateDynamicObjectPayload[] = [];

    for (const option of options) {
        const { tempId, ...optionData } = option;

        const validAttributes = Object.entries(optionData).filter(([_key, value]) => value !== null && value !== undefined && value !== '');

        if (validAttributes.length === 0) {
            // eslint-disable-next-line no-continue
            continue;
        }

        const objectValues: Record<string, any> = {};

        validAttributes.forEach(([propName, value]) => {
            const property = objectDefinition.objectProperties?.find((p) => p.name === propName);
            if (property) {
                objectValues[property.id] = value;
            } else {
                console.warn(`Property not found for ${propName} in Options object definition`);
            }
        });

        payload.push({
            tempId: tempId as number,
            objectDefinitionId: Number(objectDefinition.id),
            values: Object.entries(objectValues).map(([propertyId, value]) => ({
                propertyId: Number(propertyId),
                value: String(value)
            }))
        });
    }
    return payload;
};

/**
 * Retrieves an array of object IDs corresponding to the SifOptions' tempIds from a mapping.
 * Useful for matching bulk-created dynamic object results back to their originating option records.
 *
 * @param {SifOption[]} options - Array of option objects, each with a tempId property.
 * @param {Record<number, number>} createdObjectsByTempId - Mapping of tempId to created object ID.
 * @returns {number[]} Array of object IDs corresponding to the given options' tempIds.
 */

export const getObjectIdsByTempId = (options: SifOption[], createdObjectsByTempId: Record<number, number>): number[] => {
    const entries = Object.entries(createdObjectsByTempId);
    const ids = entries.filter(([tempId]) => options.some((option) => option.tempId === +tempId)).map(([_tempId, id]) => +id);
    return ids;
};
/**
 * Generates rows for bulk create operation from input rows.
 * Transforms row data into the format expected by the bulk create mutation.
 *
 * @param rowsToCreate - Array of row objects to transform
 * @param lineItemHeaders - Line item headers containing field definitions
 * @returns Array of LineItemRowV2Input objects ready for bulk creation
 */
export const generateRowsForBulkCreate = (rowsToCreate: Record<string, any>[], lineItemHeaders?: ILineItemHeader): LineItemRowV2Input[] => {
    const result: LineItemRowV2Input[] = [];
    for (const row of rowsToCreate) {
        const lineItems: LineItemRowItemV2Input[] = [];

        const filteredRowsKey = Object.keys(row).filter((key) => !['id', 'autogenerate', 'order'].includes(key) && row[key]);
        for (const key of filteredRowsKey) {
            const [fieldNameFromUpdated, fieldOrder] = key.split('-');
            const lineItemsByTypeFiledsId = Number(
                lineItemHeaders?.lineItemsByTypeFileds.find(
                    (el) => el.recordAdditionalFields.name === fieldNameFromUpdated && el.order === Number(fieldOrder)
                )?.id
            );
            if (lineItemsByTypeFiledsId) {
                lineItems.push({
                    value: String(row[key]),
                    lineItemsByTypeFiledsId
                });
            }
        }
        result.push({
            lineItems
        });
    }
    return result;
};
